import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as d3 from 'd3';
import {
  Globe,
  RefreshCw,
  Filter,
  AlertTriangle,
  AlertCircle,
  ShieldCheck,
  ExternalLink,
  X,
  Calendar,
  MapPin,
  Loader2,
  List,
  ChevronLeft,
  ArrowRight,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import worldMapData from '../data/world-countries.json';
import seedEventsData from '../data/eventTrackerSeed.json';

const IMPACT_LEVELS = ['Critical', 'Significant', 'Limited'];

const IMPACT_STYLES = {
  Critical: {
    fill: '#ef4444',
    stroke: '#7f1d1d',
    glow: 'rgba(239, 68, 68, 0.55)',
    chipLight: 'bg-red-50 text-red-700 border-red-200',
    chipDark: 'bg-red-900/40 text-red-200 border-red-700',
    Icon: AlertTriangle,
    description: "Direct, near-term, and material exposure to India's strategic, economic, or supply chain interests.",
  },
  Significant: {
    fill: '#f59e0b',
    stroke: '#78350f',
    glow: 'rgba(245, 158, 11, 0.55)',
    chipLight: 'bg-amber-50 text-amber-700 border-amber-200',
    chipDark: 'bg-amber-900/40 text-amber-200 border-amber-700',
    Icon: AlertCircle,
    description: "Indirect or sectoral spillover that could shape India's policy, trade, or risk posture.",
  },
  Limited: {
    fill: '#3b82f6',
    stroke: '#1e3a8a',
    glow: 'rgba(59, 130, 246, 0.45)',
    chipLight: 'bg-blue-50 text-blue-700 border-blue-200',
    chipDark: 'bg-blue-900/40 text-blue-200 border-blue-700',
    Icon: ShieldCheck,
    description: 'Background context with minor or distant relevance to India.',
  },
};

const COUNTRY_ALIAS = {
  usa: 'United States of America',
  us: 'United States of America',
  'u.s.': 'United States of America',
  'u.s.a.': 'United States of America',
  'united states': 'United States of America',
  america: 'United States of America',
  uk: 'United Kingdom',
  'u.k.': 'United Kingdom',
  britain: 'United Kingdom',
  'great britain': 'United Kingdom',
  england: 'United Kingdom',
  russia: 'Russia',
  'russian federation': 'Russia',
  'south korea': 'South Korea',
  'republic of korea': 'South Korea',
  rok: 'South Korea',
  korea: 'South Korea',
  'north korea': 'North Korea',
  dprk: 'North Korea',
  iran: 'Iran',
  'islamic republic of iran': 'Iran',
  persia: 'Iran',
  uae: 'United Arab Emirates',
  'u.a.e.': 'United Arab Emirates',
  emirates: 'United Arab Emirates',
  saudi: 'Saudi Arabia',
  ksa: 'Saudi Arabia',
  congo: 'Democratic Republic of the Congo',
  drc: 'Democratic Republic of the Congo',
  'dr congo': 'Democratic Republic of the Congo',
  'czech republic': 'Czechia',
  tanzania: 'United Republic of Tanzania',
  syria: 'Syria',
  'ivory coast': 'Ivory Coast',
  "cote d'ivoire": 'Ivory Coast',
  'cabo verde': 'Cape Verde',
  'east timor': 'East Timor',
  palestine: 'Palestine',
  taiwan: 'Taiwan',
  china: 'China',
  'hong kong': 'Hong Kong S.A.R.',
  macau: 'Macao S.A.R',
  macao: 'Macao S.A.R',
  turkey: 'Turkey',
  'türkiye': 'Turkey',
  turkiye: 'Turkey',
  myanmar: 'Myanmar',
  burma: 'Myanmar',
  eswatini: 'eSwatini',
  swaziland: 'eSwatini',
  vatican: 'Vatican City',
  'holy see': 'Vatican City',
  serbia: 'Republic of Serbia',
  bahamas: 'The Bahamas',
  gambia: 'The Gambia',
  micronesia: 'Federated States of Micronesia',
  laos: 'Laos',
  'lao pdr': 'Laos',
  brunei: 'Brunei',
  venezuela: 'Venezuela',
  bolivia: 'Bolivia',
  moldova: 'Moldova',
  kyrgyzstan: 'Kyrgyzstan',
  'kyrgyz republic': 'Kyrgyzstan',
};

const CRITICAL_PARTNER_COUNTRIES = new Set(
  [
    'china',
    'russia',
    'iran',
    'iraq',
    'saudi arabia',
    'united arab emirates',
    'qatar',
    'kuwait',
    'oman',
    'united states of america',
    'taiwan',
    'pakistan',
    'afghanistan',
    'bangladesh',
    'sri lanka',
    'myanmar',
    'nepal',
    'bhutan',
    'maldives',
    'israel',
    'turkey',
    'venezuela',
    'australia',
    'indonesia',
    'malaysia',
    'singapore',
    'south korea',
    'japan',
    'ukraine',
    'kazakhstan',
    'democratic republic of the congo',
    'south africa',
    'egypt',
    'south sudan',
    'libya',
    'yemen',
  ].map((s) => s.toLowerCase()),
);

const CRITICAL_SECTOR_KEYWORDS = [
  'energy', 'oil', 'gas', 'petroleum', 'lng',
  'mineral', 'mining', 'rare earth', 'lithium', 'cobalt', 'nickel', 'copper',
  'semiconductor', 'chip', 'technology',
  'defence', 'defense', 'security', 'military',
  'pharma', 'pharmaceutical', 'api',
  'fertili',
  'shipping', 'logistics', 'supply chain', 'maritime', 'port', 'corridor',
  'finance', 'banking', 'monetary', 'currency',
];

const CRITICAL_EVENT_TYPES = new Set([
  'conflict',
  'sanctions',
  'chokepoint_disruption',
  'strategic_dependency',
  'grey_zone_coercion',
]);

const SIGNIFICANT_EVENT_TYPES = new Set([
  'trade_policy',
  'supply_chain',
  'regulatory_cascade',
  'alliance',
  'political_event',
  'technology',
]);

function normalizeCountryName(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const cleaned = raw.trim().toLowerCase().replace(/[\u2018\u2019]/g, "'");
  if (!cleaned) return null;
  return COUNTRY_ALIAS[cleaned] || raw.trim();
}

function buildCentroidMap(featureCollection) {
  const map = new Map();
  const path = d3.geoPath();
  for (const feature of featureCollection.features) {
    const name = feature.properties?.name;
    if (!name) continue;
    let centroid = null;
    try {
      centroid = d3.geoCentroid(feature);
    } catch (_) {
      centroid = null;
    }
    if (!centroid || Number.isNaN(centroid[0]) || Number.isNaN(centroid[1])) {
      try {
        const c = path.centroid(feature);
        if (c && !Number.isNaN(c[0]) && !Number.isNaN(c[1])) {
          centroid = [c[0], c[1]];
        }
      } catch (_) {
        centroid = null;
      }
    }
    if (centroid) {
      const key = name.toLowerCase();
      map.set(key, { name, lonLat: centroid });
      const iso3 = feature.properties?.['ISO3166-1-Alpha-3'];
      const iso2 = feature.properties?.['ISO3166-1-Alpha-2'];
      if (iso3) map.set(iso3.toLowerCase(), { name, lonLat: centroid });
      if (iso2) map.set(iso2.toLowerCase(), { name, lonLat: centroid });
    }
  }
  return map;
}

function classifyImpactOnIndia(event) {
  const enrichment = event?.enrichment || {};
  const entities = event?.entities || {};
  const countries = (entities.countries || []).map((c) => String(c).toLowerCase());
  const stakeholders = (enrichment.affected_stakeholders || []).map((s) => String(s).toLowerCase());
  const sectors = (enrichment.related_sectors || []).map((s) => String(s).toLowerCase());
  const eventType = String(event?.event_type || enrichment.event_category || '').toLowerCase();
  const headline = String(event?.headline || '').toLowerCase();

  const mentionsIndia =
    countries.some((c) => c === 'india' || c.includes('india')) ||
    stakeholders.some((s) => s.includes('india')) ||
    headline.includes('india');

  const involvesCriticalPartner =
    countries.some((c) => CRITICAL_PARTNER_COUNTRIES.has(c)) ||
    stakeholders.some((s) => Array.from(CRITICAL_PARTNER_COUNTRIES).some((cp) => s.includes(cp)));

  const matchesCriticalSector = sectors.some((s) =>
    CRITICAL_SECTOR_KEYWORDS.some((k) => s.includes(k)),
  );

  const chokepoint = enrichment?.chokepoint_leverage || {};
  const hasChokepointLeverage =
    chokepoint?.relevant === true &&
    Array.isArray(chokepoint?.chokepoints_involved) &&
    chokepoint.chokepoints_involved.length > 0;

  const greyZone = enrichment?.grey_zone_indicators || {};
  const hasGreyZone = greyZone?.present === true;

  const cascade = enrichment?.regulatory_cascade_risk || {};
  const hasRegulatoryCascade = cascade?.present === true;

  const disruptionStatus = String(enrichment?.disruption_spectrum?.status || '').toLowerCase();
  const isHardDisruption =
    disruptionStatus === 'operationally_compromised' ||
    disruptionStatus === 'functionally_denied';

  if (mentionsIndia) return 'Critical';
  if (CRITICAL_EVENT_TYPES.has(eventType) && involvesCriticalPartner) return 'Critical';
  if (hasChokepointLeverage && involvesCriticalPartner) return 'Critical';
  if (isHardDisruption && involvesCriticalPartner) return 'Critical';

  if (matchesCriticalSector && involvesCriticalPartner) return 'Significant';
  if (hasGreyZone || hasRegulatoryCascade) return 'Significant';
  if (CRITICAL_EVENT_TYPES.has(eventType)) return 'Significant';
  if (SIGNIFICANT_EVENT_TYPES.has(eventType) && involvesCriticalPartner) return 'Significant';

  return 'Limited';
}

function buildDots(events, centroidMap) {
  const dots = [];
  for (const ev of events) {
    const enrichmentCountries = ev?.enrichment?.key_entities?.countries || [];
    const entityCountries = ev?.entities?.countries || [];
    const merged = [...new Set([...entityCountries, ...enrichmentCountries].filter(Boolean))];
    const impact = classifyImpactOnIndia(ev);

    const located = [];
    for (const raw of merged) {
      const normalized = normalizeCountryName(raw);
      if (!normalized) continue;
      const direct = centroidMap.get(normalized.toLowerCase());
      const fuzzy =
        direct ||
        centroidMap.get(raw.toLowerCase()) ||
        Array.from(centroidMap.values()).find((entry) =>
          entry.name.toLowerCase().includes(normalized.toLowerCase()),
        );
      if (fuzzy && !located.find((c) => c.name === fuzzy.name)) {
        located.push(fuzzy);
      }
    }

    if (located.length === 0) continue;

    located.forEach((country, idx) => {
      dots.push({
        id: `${ev.event_id || ev.headline || 'evt'}-${country.name}-${idx}`,
        eventId: ev.event_id,
        country: country.name,
        lonLat: country.lonLat,
        impact,
        event: ev,
      });
    });
  }
  return dots;
}

/**
 * Materialise the bundled seed events with timestamps that always fall within the
 * last 7 days, so the visual stays meaningful even when the backend pipeline is
 * still warming up. Events are sorted newest first to match the API contract.
 */
function loadSeedEvents() {
  const items = Array.isArray(seedEventsData?.events) ? seedEventsData.events : [];
  const now = Date.now();
  const materialised = items.map((raw) => {
    const daysAgo = Number.isFinite(raw?.daysAgo) ? raw.daysAgo : 0;
    const hoursAgo = Number.isFinite(raw?.hoursAgo) ? raw.hoursAgo : 0;
    const ts = now - daysAgo * 24 * 60 * 60 * 1000 - hoursAgo * 60 * 60 * 1000;
    const { daysAgo: _da, hoursAgo: _ha, ...rest } = raw || {};
    return {
      ...rest,
      timestamp: new Date(ts).toISOString(),
      data_source: rest?.data_source || 'seed',
    };
  });
  materialised.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return materialised;
}

const EventTracker = () => {
  const { isDarkMode } = useTheme();

  // While the AI pipeline is being stabilised the page is wired to use the
  // bundled seed dataset. Re-introduce the live API call in `handleRefresh`
  // / `useEffect(loadEvents, ...)` when the backend is ready again.
  const [events, setEvents] = useState(() => loadSeedEvents());
  const [refreshing, setRefreshing] = useState(false);
  const [selectedImpacts, setSelectedImpacts] = useState(new Set(IMPACT_LEVELS));
  const [activeDot, setActiveDot] = useState(null);
  const [hover, setHover] = useState(null); // { dot, x, y }
  const [filterOpen, setFilterOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1280, height: 720 });

  const centroidMap = useMemo(() => buildCentroidMap(worldMapData), []);

  const dots = useMemo(() => buildDots(events, centroidMap), [events, centroidMap]);

  const filteredDots = useMemo(
    () => dots.filter((d) => selectedImpacts.has(d.impact)),
    [dots, selectedImpacts],
  );

  const counts = useMemo(() => {
    const result = { Critical: 0, Significant: 0, Limited: 0 };
    for (const d of dots) {
      result[d.impact] = (result[d.impact] || 0) + 1;
    }
    return result;
  }, [dots]);

  // Track container size
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const update = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.max(640, Math.floor(rect.width));
      const height = Math.max(420, Math.floor(rect.height));
      setDimensions({ width, height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const projection = useMemo(() => {
    const proj = d3.geoNaturalEarth1();
    try {
      proj.fitSize([dimensions.width, dimensions.height + 60], {
        type: 'Sphere',
      });
    } catch (_) {
      proj.scale(Math.min(dimensions.width / 6.2, 240)).translate([dimensions.width / 2, dimensions.height / 2]);
    }
    // shift slightly up so labels at top don't overlap title
    const t = proj.translate();
    proj.translate([t[0], t[1] - 10]);
    return proj;
  }, [dimensions]);

  // Render base map
  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', dimensions.width).attr('height', dimensions.height);

    const oceanColor = isDarkMode ? '#0b1220' : '#dbeafe';
    const landColor = isDarkMode ? '#1e293b' : '#ffffff';
    const indiaColor = isDarkMode ? '#22c55e' : '#4ade80';
    const indiaStroke = isDarkMode ? '#15803d' : '#16a34a';
    const borderColor = isDarkMode ? '#334155' : '#94a3b8';
    const graticuleColor = isDarkMode ? 'rgba(148, 163, 184, 0.07)' : 'rgba(100, 116, 139, 0.18)';

    svg
      .append('rect')
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .attr('fill', oceanColor);

    const path = d3.geoPath().projection(projection);
    const graticule = d3.geoGraticule().step([15, 15]);

    svg
      .append('path')
      .datum(graticule())
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', graticuleColor)
      .attr('stroke-width', 0.6);

    svg
      .append('g')
      .selectAll('path')
      .data(worldMapData.features)
      .enter()
      .append('path')
      .attr('d', path)
      .attr('fill', (d) => (d.properties?.name === 'India' ? indiaColor : landColor))
      .attr('stroke', (d) => (d.properties?.name === 'India' ? indiaStroke : borderColor))
      .attr('stroke-width', (d) => (d.properties?.name === 'India' ? 0.8 : 0.4));
  }, [projection, dimensions, isDarkMode]);

  const handleToggleImpact = (level) => {
    setSelectedImpacts((prev) => {
      const next = new Set(prev);
      if (next.has(level)) next.delete(level);
      else next.add(level);
      if (next.size === 0) return new Set(IMPACT_LEVELS);
      return next;
    });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    // Re-materialise seed events so timestamps roll forward to "now".
    setEvents(loadSeedEvents());
    window.setTimeout(() => setRefreshing(false), 350);
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (_) {
      return ts;
    }
  };

  const overlayCardClass = `backdrop-blur-md border ${
    isDarkMode
      ? 'bg-gray-900/80 border-gray-700/70 text-white'
      : 'bg-white/90 border-gray-200/80 text-gray-900 shadow-sm'
  }`;
  const overlayBtnClass = `inline-flex items-center justify-center w-10 h-10 rounded-full ${overlayCardClass} transition ${
    isDarkMode ? 'hover:bg-gray-800/80' : 'hover:bg-gray-100/90'
  }`;
  const overlayRingClass = isDarkMode ? 'ring-2 ring-white/40' : 'ring-2 ring-gray-400/50';
  const overlayMutedClass = isDarkMode ? 'text-gray-300' : 'text-gray-500';
  const overlaySubtleClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const dotStroke = isDarkMode
    ? { active: '#ffffff', inactive: 'rgba(255,255,255,0.85)' }
    : { active: '#1e293b', inactive: 'rgba(30,41,59,0.75)' };

  return (
    <div
      className={`relative w-full ${isDarkMode ? 'bg-[#0b1220]' : 'bg-slate-100'}`}
      style={{ height: 'calc(100vh - 64px)' }}
    >
      <div className="absolute inset-0" ref={containerRef}>
        <svg ref={svgRef} className="block w-full h-full" />
        <svg
          className="absolute top-0 left-0 pointer-events-none"
          width={dimensions.width}
          height={dimensions.height}
        >
          {filteredDots.map((dot) => {
            const projected = projection(dot.lonLat);
            if (!projected || Number.isNaN(projected[0]) || Number.isNaN(projected[1])) {
              return null;
            }
            const [x, y] = projected;
            const style = IMPACT_STYLES[dot.impact];
            const isActive = activeDot && activeDot.id === dot.id;
            const updateHover = (e) => {
              const rect = containerRef.current?.getBoundingClientRect();
              const offsetX = rect ? e.clientX - rect.left + 14 : 0;
              const offsetY = rect ? e.clientY - rect.top + 14 : 0;
              setHover({ dot, x: offsetX, y: offsetY });
            };
            return (
              <g
                key={dot.id}
                style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                onMouseEnter={updateHover}
                onMouseMove={updateHover}
                onMouseLeave={() => setHover(null)}
                onClick={() => {
                  setActiveDot(dot);
                  setHover(null);
                }}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={isActive ? 14 : 12}
                  fill={style.glow}
                  opacity={0.45}
                  style={{ pointerEvents: 'none' }}
                >
                  <animate
                    attributeName="r"
                    values={`${isActive ? 11 : 9};${isActive ? 20 : 18};${isActive ? 11 : 9}`}
                    dur="2.6s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.55;0.05;0.55"
                    dur="2.6s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle
                  cx={x}
                  cy={y}
                  r={isActive ? 8 : 6.5}
                  fill={style.fill}
                  stroke={isActive ? dotStroke.active : dotStroke.inactive}
                  strokeWidth={isActive ? 2 : 1.4}
                />
              </g>
            );
          })}
        </svg>

        {hover?.dot && !activeDot && (
          <div
            className={`absolute z-30 max-w-xs px-3 py-2 rounded-lg shadow-lg text-xs pointer-events-none border ${
              isDarkMode
                ? 'bg-gray-900/95 text-gray-100 border-gray-700'
                : 'bg-white/95 text-gray-900 border-gray-200'
            }`}
            style={{ left: hover.x, top: hover.y }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: IMPACT_STYLES[hover.dot.impact].fill }}
              />
              <span className={`text-[10px] font-bold uppercase tracking-wider ${overlayMutedClass}`}>
                {hover.dot.country} · {hover.dot.impact}
              </span>
            </div>
            <div className="font-medium leading-snug line-clamp-3">
              {hover.dot.event?.headline}
            </div>
          </div>
        )}
      </div>

      {/* Top-left controls (filter + list) */}
      <div className="absolute top-4 left-4 flex flex-col gap-2 z-20">
        <button
          type="button"
          onClick={() => {
            setFilterOpen((v) => !v);
            setListOpen(false);
          }}
          className={`${overlayBtnClass} ${filterOpen ? overlayRingClass : ''}`}
          aria-label="Toggle filters"
          title="Filters"
        >
          <Filter className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            setListOpen((v) => !v);
            setFilterOpen(false);
          }}
          className={`${overlayBtnClass} ${listOpen ? overlayRingClass : ''}`}
          aria-label="Toggle event list"
          title="Event list"
        >
          <List className="h-4 w-4" />
        </button>

        {filterOpen && (
          <div className={`mt-1 p-3 rounded-xl ${overlayCardClass} min-w-[220px]`}>
            <div className="flex items-center justify-between mb-2">
              <div className={`text-xs font-semibold uppercase tracking-wider ${overlayMutedClass}`}>
                Impact on India
              </div>
              <button
                type="button"
                onClick={() => setFilterOpen(false)}
                className={`${overlaySubtleClass} ${
                  isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'
                }`}
                aria-label="Close filters"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex flex-col gap-1.5">
              {IMPACT_LEVELS.map((level) => {
                const style = IMPACT_STYLES[level];
                const Icon = style.Icon;
                const active = selectedImpacts.has(level);
                return (
                  <button
                    key={level}
                    type="button"
                    onClick={() => handleToggleImpact(level)}
                    className={`flex items-center justify-between w-full px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      active
                        ? isDarkMode
                          ? 'bg-white/10 border-white/20 text-white'
                          : 'bg-gray-100 border-gray-300 text-gray-900'
                        : isDarkMode
                          ? 'bg-transparent border-white/10 text-gray-400'
                          : 'bg-transparent border-gray-200 text-gray-500'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{
                          backgroundColor: active ? style.fill : 'transparent',
                          borderColor: style.fill,
                          borderWidth: 1,
                          borderStyle: 'solid',
                        }}
                      />
                      <Icon className="h-3.5 w-3.5" />
                      <span>{level}</span>
                    </span>
                    <span className={`text-[10px] ${overlayMutedClass}`}>{counts[level] || 0}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Title overlay (top-center) */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center z-10 pointer-events-none">
        <div className={`inline-flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.2em] ${overlayMutedClass}`}>
          <Globe className="h-3 w-3" />
          <span>Geoeconomic Pulse</span>
        </div>
        <h1
          className={`text-2xl md:text-3xl font-bold drop-shadow-lg ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}
        >
          Event Tracker
        </h1>
        <p className={`text-xs md:text-sm mt-1 max-w-xl mx-auto ${overlayMutedClass}`}>
          Geopolitical events from the last 7 days · click a dot for full event detail
        </p>
      </div>

      {/* Top-right: refresh */}
      <div className="absolute top-4 right-4 flex flex-col items-end gap-2 z-20">
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className={`${overlayBtnClass} disabled:opacity-60`}
          aria-label="Refresh"
          title="Refresh"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      {/* Bottom-left legend */}
      <div className={`absolute bottom-4 left-4 px-3 py-2 rounded-xl ${overlayCardClass} z-10`}>
        <div className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${overlayMutedClass}`}>
          Impact on India
        </div>
        <div className="flex items-center gap-3">
          {IMPACT_LEVELS.map((lvl) => (
            <div key={`legend-${lvl}`} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: IMPACT_STYLES[lvl].fill }}
              />
              <span className="text-[11px]">{lvl}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom-right stats */}
      <div className={`absolute bottom-4 right-4 px-3 py-2 rounded-xl ${overlayCardClass} z-10 text-[11px] flex items-center gap-2`}>
        <span>
          <Calendar className="inline h-3 w-3 mr-1 -mt-0.5" />
          Last 7 days · {events.length} event{events.length === 1 ? '' : 's'} · {filteredDots.length} dot
          {filteredDots.length === 1 ? '' : 's'}
        </span>
        <span
          className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
            isDarkMode
              ? 'bg-amber-500/20 text-amber-200 border-amber-500/40'
              : 'bg-amber-100 text-amber-800 border-amber-300'
          }`}
          title="Showing the bundled sample dataset. Live AI pipeline integration is paused for now."
        >
          Sample data
        </span>
      </div>

      {/* Slide-in event list */}
      {listOpen && (
        <div
          className={`absolute top-0 left-0 h-full w-80 max-w-[80vw] z-30 flex flex-col backdrop-blur-md ${
            isDarkMode
              ? 'bg-gray-900/95 border-r border-gray-700'
              : 'bg-white/95 border-r border-gray-200 shadow-lg'
          }`}
        >
          <EventList
            dots={filteredDots}
            onSelect={(dot) => {
              setActiveDot(dot);
            }}
            onClose={() => setListOpen(false)}
            formatDate={formatDate}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {/* Modal */}
      {activeDot &&
        createPortal(
          <EventModal
            dot={activeDot}
            onClose={() => setActiveDot(null)}
            isDarkMode={isDarkMode}
            formatDate={formatDate}
          />,
          document.body,
        )}
    </div>
  );
};

const EventList = ({ dots, onSelect, onClose, formatDate, isDarkMode }) => {
  const uniqueByEvent = useMemo(() => {
    const seen = new Map();
    for (const d of dots) {
      const key = d.eventId || d.event?.headline;
      if (!key) continue;
      if (!seen.has(key)) seen.set(key, d);
    }
    const ranked = Array.from(seen.values());
    const order = { Critical: 0, Significant: 1, Limited: 2 };
    ranked.sort((a, b) => {
      const ord = (order[a.impact] ?? 3) - (order[b.impact] ?? 3);
      if (ord !== 0) return ord;
      const ta = new Date(a.event?.timestamp || 0).getTime();
      const tb = new Date(b.event?.timestamp || 0).getTime();
      return tb - ta;
    });
    return ranked;
  }, [dots]);

  const dividerClass = isDarkMode ? 'border-white/10' : 'border-gray-200';
  const headingClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const mutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const hoverClass = isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50';

  return (
    <>
      <div className={`px-4 py-3 border-b ${dividerClass} flex items-center justify-between`}>
        <div>
          <div className={`text-sm font-semibold ${headingClass}`}>Events this week</div>
          <div className={`text-[11px] ${mutedClass}`}>
            {uniqueByEvent.length} unique event{uniqueByEvent.length === 1 ? '' : 's'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`p-1 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          aria-label="Close list"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {uniqueByEvent.length === 0 ? (
          <div className={`p-4 text-sm ${mutedClass}`}>No events match the current filter.</div>
        ) : (
          uniqueByEvent.map((dot) => {
            const style = IMPACT_STYLES[dot.impact];
            return (
              <button
                key={dot.id}
                type="button"
                onClick={() => onSelect(dot)}
                className={`w-full text-left px-4 py-3 border-b ${dividerClass} flex gap-3 items-start ${hoverClass} transition`}
              >
                <span
                  className="mt-1.5 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: style.fill }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border"
                      style={{
                        borderColor: style.fill,
                        color: style.fill,
                        backgroundColor: `${style.fill}25`,
                      }}
                    >
                      {dot.impact}
                    </span>
                    <span className={`text-[11px] ${mutedClass}`}>
                      <MapPin className="inline h-3 w-3 -mt-0.5" /> {dot.country}
                    </span>
                  </div>
                  <div className={`text-sm font-medium mt-1 line-clamp-2 ${headingClass}`}>
                    {dot.event?.headline}
                  </div>
                  <div className={`text-[11px] mt-1 ${mutedClass}`}>
                    {formatDate(dot.event?.timestamp)} · {dot.event?.source || 'Unknown source'}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </>
  );
};

const EventModal = ({ dot, onClose, isDarkMode, formatDate }) => {
  const event = dot.event || {};
  const enrichment = event.enrichment || {};
  const style = IMPACT_STYLES[dot.impact];

  const stakeholders = Array.isArray(enrichment.affected_stakeholders) ? enrichment.affected_stakeholders : [];
  const sectors = Array.isArray(enrichment.related_sectors) ? enrichment.related_sectors : [];
  const secondOrder = Array.isArray(enrichment.second_order_effects_en) && enrichment.second_order_effects_en.length > 0
    ? enrichment.second_order_effects_en
    : Array.isArray(enrichment.second_order_effects)
    ? enrichment.second_order_effects
    : [];
  const significance = enrichment.strategic_significance_en || enrichment.strategic_significance || '';
  const leverage = enrichment.economic_leverage_shifts_en || enrichment.economic_leverage_shifts || '';
  const eventCategory = String(event.event_type || enrichment.event_category || 'event').replace(/_/g, ' ');

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cardClass = isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const headingClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const bodyClass = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const mutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const dividerClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        role="presentation"
      />
      <div className={`relative max-w-2xl w-full max-h-[88vh] rounded-2xl border shadow-2xl overflow-hidden flex flex-col ${cardClass}`}>
        {/* Accent bar */}
        <div className="h-1.5 w-full" style={{ backgroundColor: style.fill }} />
        <div className={`px-5 pt-4 pb-3 border-b ${dividerClass}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border"
                  style={{
                    borderColor: style.fill,
                    color: style.fill,
                    backgroundColor: `${style.fill}15`,
                  }}
                >
                  Impact on India · {dot.impact}
                </span>
                <span className={`text-[11px] uppercase tracking-wider ${mutedClass}`}>
                  {eventCategory}
                </span>
              </div>
              <h3 className={`text-lg md:text-xl font-bold mt-2 leading-snug ${headingClass}`}>
                {event.headline}
              </h3>
              <div className={`text-xs mt-1.5 flex items-center gap-3 ${mutedClass}`}>
                <span>
                  <MapPin className="inline h-3 w-3 -mt-0.5" /> {dot.country}
                </span>
                <span>
                  <Calendar className="inline h-3 w-3 -mt-0.5" /> {formatDate(event.timestamp)}
                </span>
                {event.source && <span>· {event.source}</span>}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-lg ${
                isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'
              }`}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {significance && (
            <Section title="Strategic significance" body={significance} headingClass={headingClass} bodyClass={bodyClass} />
          )}
          {leverage && (
            <Section title="Economic leverage shifts" body={leverage} headingClass={headingClass} bodyClass={bodyClass} />
          )}
          {stakeholders.length > 0 && (
            <ListSection
              title="Affected stakeholders"
              items={stakeholders}
              isDarkMode={isDarkMode}
              headingClass={headingClass}
              mutedClass={mutedClass}
            />
          )}
          {sectors.length > 0 && (
            <ListSection
              title="Related sectors"
              items={sectors}
              isDarkMode={isDarkMode}
              headingClass={headingClass}
              mutedClass={mutedClass}
            />
          )}
          {secondOrder.length > 0 && (
            <div>
              <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${headingClass} opacity-70`}>
                Ripple effects
              </div>
              <ul className="space-y-1.5">
                {secondOrder.map((eff, idx) => (
                  <li key={`eff-${idx}`} className={`text-sm flex gap-2 items-start ${bodyClass}`}>
                    <span
                      className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: style.fill }}
                    />
                    <span>{eff}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {event.source_url && (
          <div className={`px-5 py-3 border-t ${dividerClass}`}>
            <a
              href={event.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
                isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              Read source article
              <ArrowRight className="h-3.5 w-3.5" />
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, body, headingClass, bodyClass }) => (
  <div>
    <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${headingClass} opacity-70`}>
      {title}
    </div>
    <p className={`text-sm leading-relaxed ${bodyClass}`}>{body}</p>
  </div>
);

const ListSection = ({ title, items, isDarkMode, headingClass, mutedClass }) => (
  <div>
    <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${headingClass} opacity-70`}>
      {title}
    </div>
    <div className="flex flex-wrap gap-1.5">
      {items.slice(0, 16).map((item, idx) => (
        <span
          key={`tag-${title}-${idx}`}
          className={`text-[11px] px-2 py-0.5 rounded-full border ${
            isDarkMode
              ? 'bg-gray-800 border-gray-700 text-gray-200'
              : 'bg-gray-100 border-gray-200 text-gray-700'
          }`}
        >
          {String(item).replace(/_/g, ' ')}
        </span>
      ))}
      {items.length > 16 && (
        <span className={`text-[11px] ${mutedClass}`}>+{items.length - 16} more</span>
      )}
    </div>
  </div>
);

export default EventTracker;
